import React, { useState, useEffect, useRef, useCallback } from 'react';
import { lazyWithReload } from './utils/lazyWithReload';
import { Analytics } from '@vercel/analytics/react';
import { Button } from './components/ui/Button';
import { ProfileRegistration } from './components/ProfileRegistration';
import { RegularizeDebt } from './components/RegularizeDebt';
import { LeagueProvider } from './context/LeagueProvider';
import { ViewerLeagueProvider } from './context/ViewerLeagueProvider';
import { SurveyStatusProvider } from './context/SurveyStatusProvider';
import { MandatorySurveyGate } from './components/user/MandatorySurveyGate';
// PWA del participante: pantallas cargadas on-demand (React.lazy). El bundle
// inicial también sirve las rutas PÚBLICAS (/ y /liga/*), así que mantener estas
// pantallas fuera del chunk de entrada evita descargar toda la app del
// participante (incluidos framer-motion, react-markdown, etc.) en el primer paint
// del público. Cada pantalla es su propio chunk; se resuelven bajo <Suspense>.
const Layout = lazyWithReload(() => import('./components/Layout').then(m => ({ default: m.Layout })));
const Registration = lazyWithReload(() => import('./components/Registration').then(m => ({ default: m.Registration })));
const RegistrationSuccess = lazyWithReload(() => import('./components/RegistrationSuccess').then(m => ({ default: m.RegistrationSuccess })));
const Dashboard = lazyWithReload(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const PreSeasonDashboard = lazyWithReload(() => import('./components/PreSeasonDashboard').then(m => ({ default: m.PreSeasonDashboard })));
const Standings = lazyWithReload(() => import('./components/Standings').then(m => ({ default: m.Standings })));
const MatchCenter = lazyWithReload(() => import('./components/MatchCenter').then(m => ({ default: m.MatchCenter })));
const RecoveryScreen = lazyWithReload(() => import('./components/RecoveryScreen').then(m => ({ default: m.RecoveryScreen })));
const ReplacedPlayerScreen = lazyWithReload(() => import('./components/ReplacedPlayerScreen').then(m => ({ default: m.ReplacedPlayerScreen })));
const UnpublishedLeagueScreen = lazyWithReload(() => import('./components/UnpublishedLeagueScreen').then(m => ({ default: m.UnpublishedLeagueScreen })));
const SeasonEndedScreen = lazyWithReload(() => import('./components/SeasonEndedScreen').then(m => ({ default: m.SeasonEndedScreen })));
const AdminDashboard = lazyWithReload(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminLogin = lazyWithReload(() => import('./components/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const VenueRedirectScreen = lazyWithReload(() => import('./components/admin/VenueRedirectScreen').then(m => ({ default: m.VenueRedirectScreen })));
// Lazy: el sitio público es un "app" aparte; el PWA participante instalado NUNCA
// lo carga (ver guard isInstalledPWA en el routing) → fuera del bundle principal.
const PublicRouter = lazyWithReload(() => import('./components/public/PublicRouter').then(m => ({ default: m.PublicRouter })));
const SubstituteRegistration = lazyWithReload(() => import('./components/SubstituteRegistration').then(m => ({ default: m.SubstituteRegistration })));
const Fixture = lazyWithReload(() => import('./components/Fixture').then(m => ({ default: m.Fixture })));
const ScheduleMatch = lazyWithReload(() => import('./components/ScheduleMatch').then(m => ({ default: m.ScheduleMatch })));
const TeamView = lazyWithReload(() => import('./components/TeamView').then(m => ({ default: m.TeamView })));
const ProfileView = lazyWithReload(() => import('./components/Profile').then(m => ({ default: m.Profile })));
const ConveniosInfo = lazyWithReload(() => import('./components/ConveniosInfo').then(m => ({ default: m.ConveniosInfo })));
// Pantallas de bloqueo (desktop / in-app browser): se mantienen estáticas porque
// son livianas y deben aparecer al instante, sin fetch de un chunk extra.
import { BrowserBlockScreen } from './components/BrowserBlockScreen';
import { InAppBrowserScreen } from './components/InAppBrowserScreen';
import MobileOnlyScreen from './components/MobileOnlyScreen';
import { NewEditionCta } from './components/NewEditionCta';
import { RulebookCta } from './components/RulebookCta';
import { PushNotificationReminder } from './components/PushNotificationReminder';
const RulebookReacceptanceGate = lazyWithReload(() => import('./components/RulebookReacceptanceGate').then(m => ({ default: m.RulebookReacceptanceGate })));
import { OpenerAuthProvider, useOpenerAuth } from './context/OpenerAuthProvider';
// Lazy: apps por rol que el participante NUNCA usa (opener / recepción / preview /
// sponsor) → fuera del bundle principal del participante.
const MatchOpenerLogin = lazyWithReload(() => import('./components/opener/MatchOpenerLogin').then(m => ({ default: m.MatchOpenerLogin })));
const MatchOpenerControl = lazyWithReload(() => import('./components/opener/MatchOpenerControl').then(m => ({ default: m.MatchOpenerControl })));
const StaffConnectionError = lazyWithReload(() => import('./components/shared/StaffConnectionError').then(m => ({ default: m.StaffConnectionError })));
const ReceptionistPage = lazyWithReload(() => import('./pages/ReceptionistPage'));
const VerificationPage = lazyWithReload(() => import('./pages/VerificationPage'));
const SponsorManagerPage = lazyWithReload(() => import('./pages/SponsorManagerPage'));
const PreviewAlertaPresentePage = lazyWithReload(() => import('./pages/PreviewAlertaPresentePage'));
const PreviewMiDiaPage = lazyWithReload(() => import('./pages/PreviewMiDiaPage'));
const CredentialQrRedirect = lazyWithReload(() => import('./pages/CredentialQrRedirect'));
const RaffleJoinPage = lazyWithReload(() => import('./pages/RaffleJoinPage'));
const RaffleQrScreenPage = lazyWithReload(() => import('./pages/RaffleQrScreenPage'));
const PreviewOpenerPage = lazyWithReload(() => import('./pages/PreviewOpenerPage'));
const PreviewReceptionPage = lazyWithReload(() => import('./pages/PreviewReceptionPage'));
const PreviewBetweenSeasonsPage = lazyWithReload(() => import('./pages/PreviewBetweenSeasonsPage'));
const PreviewPlacasPage = lazyWithReload(() => import('./pages/PreviewPlacasPage'));
// v6.11.235 — Página que fotografía el worker de historias automáticas.
const RenderStoryPage = lazyWithReload(() => import('./pages/RenderStoryPage'));
const PreviewNovedadesPage = lazyWithReload(() => import('./pages/PreviewNovedadesPage'));
const PreviewCredencialPage = lazyWithReload(() => import('./pages/PreviewCredencialPage'));
const PreviewSimulacroPage = lazyWithReload(() => import('./pages/PreviewSimulacroPage'));
const PreviewExpiredProposalPage = lazyWithReload(() => import('./pages/PreviewExpiredProposalPage'));
const PreviewMatchCardPage = lazyWithReload(() => import('./pages/PreviewMatchCardPage'));
const SponsorRegistrationForm = lazyWithReload(() => import('./components/public/SponsorRegistrationForm').then(m => ({ default: m.SponsorRegistrationForm })));
const SponsorPortal = lazyWithReload(() => import('./components/sponsor/SponsorPortal').then(m => ({ default: m.SponsorPortal })));
// Lazy: el simulacro se hace UNA vez por persona, así que no tiene por qué
// viajar en el bundle que se descarga en cada apertura.
const Simulacro = lazyWithReload(() => import('./components/simulacro/Simulacro').then(m => ({ default: m.Simulacro })));
import { useAuth } from './context/AuthProvider';
import { getAppSettings } from './services/api/settings';
import { isReservedSlug } from './constants/reservedSlugs';
import { parseRaffleQrPath } from './utils/raffleLinks';

// v6.11.15.00 — Detecta un slug root-level (`/liga-ajupam-5ra`) para catch-all
// de landing por-edición. Cualquier segmento reservado (login, admin, campeones,
// api, etc.) o multi-segmento devuelve false y sigue el flujo normal.
const ROOT_SLUG_RE = /^\/([a-z0-9][a-z0-9-]*[a-z0-9])\/?$/;
function isRootLevelLeagueSlug(path: string): boolean {
  const m = path.match(ROOT_SLUG_RE);
  if (!m) return false;
  return !isReservedSlug(m[1]);
}

// v6.11.225 — Destino pendiente del link `/evento/<id>` mientras el jugador
// pasa por el ingreso por DNI. sessionStorage: muere con la pestaña, así que no
// arrastra el evento a un ingreso de otro día.
const PENDING_EVENT_KEY = 'pending_event_id';
function rememberPendingEvent(eventId: string): void {
  try { sessionStorage.setItem(PENDING_EVENT_KEY, eventId); } catch { /* storage bloqueado */ }
}
function takePendingEvent(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_EVENT_KEY);
    if (id) sessionStorage.removeItem(PENDING_EVENT_KEY);
    return id;
  } catch {
    return null;
  }
}
import { getPlayerEntryOptions } from './services/api/auth';
const PlayerLeagueHub = lazyWithReload(() => import('./components/PlayerLeagueHub').then(m => ({ default: m.PlayerLeagueHub })));
// v6.11.225 — Pantalla de un evento intermedio (`/evento/<id>` o la tarjeta del Inicio).
const LeagueEventScreen = lazyWithReload(() => import('./components/events/LeagueEventScreen').then(m => ({ default: m.LeagueEventScreen })));
import type { PlayerEntryOptions, PlayerLeagueOption, OpenRegistrationOption, ReplacedLeagueOption } from './types';
import { getLogoFilter } from './utils/logoTreatment';

const DEFAULT_LIGA_LOGO = 'https://muincpkiwzhuhqfhgzfa.supabase.co/storage/v1/object/public/APP/Logos/LigaAjupamTandem-fotos-neg.svg';

// Fallback de pantalla completa para las rutas lazy (spinner sutil sobre el bg).
const ROUTE_FALLBACK = (
  <div className="min-h-screen flex items-center justify-center bg-bg">
    <div className="animate-spin h-8 w-8 border-2 border-primary/30 border-t-primary rounded-full" />
  </div>
);
import { getCurrentLeague, getCurrentLeagueByFamily, getLeagueBySlug, getPublicPreRegistrationLeagues } from './services/api/public';
import { validateAdminSession, logoutAdmin } from './services/api/adminAuth';
import { getCurrentSession } from './services/api/auth';
const PlayoffBracketView = lazyWithReload(() => import('./components/user/PlayoffBracketView').then(m => ({ default: m.PlayoffBracketView })));
const PartidosTabs = lazyWithReload(() => import('./components/user/PartidosTabs').then(m => ({ default: m.PartidosTabs })));
import { AppSettings } from './types';
import { ArrowRight, MessageCircle, Megaphone, Info, AlertTriangle, CheckCircle, Eye } from 'lucide-react';
import { supabase } from './services/api/client';
import './utils/testSupabase'; // Test de conexión disponible en consola
import { AnnouncementsCarousel } from './components/AnnouncementsCarousel';
const LoginAnnouncementsCarousel = lazyWithReload(() => import('./components/LoginAnnouncementsCarousel').then(m => ({ default: m.LoginAnnouncementsCarousel })));
import { loadDictionariesFromDB } from './utils/nameNormalization';
import { isInstalledPWA, isMobileDevice, isInAppBrowser } from './utils/deviceUtils';
import { trackAppView, setAppContext, resetAppUser } from './utils/appAnalytics';
import { emitLeagueDataUpdated } from './utils/leagueDataEvents';
import { resolveBlockState } from './utils/blockState';
import { resolveSectionAccess } from './utils/sectionAccess';
import { useSimulacroGate } from './hooks/useSimulacroGate';
import { markSimulacroCompleted } from './services/api/simulacro';
import { SIMULACRO_VERSION } from './data/simulacroScenes';
import { trackAppEvent } from './utils/appAnalytics';
import type { NavigateFn, NavigateOptions } from './types/navigation';
import { resolveDeepLinkPath } from './utils/notificationTargets';
import { LockedSectionOverlay } from './components/LockedSectionOverlay';
import { useAppResume } from './hooks/useAppResume';

const AnnouncementsList = () => {
  const [announcements, setAnnouncements] = useState<any[]>([]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const { data } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (data) setAnnouncements(data);
    };
    fetchAnnouncements();
  }, []);

  if (announcements.length === 0) return null;

  return (
    <div className="w-full max-w-sm space-y-3 mb-8">
      {announcements.map((ann) => (
        <div 
          key={ann.id} 
          className={`p-4 rounded-2xl border-l-4 shadow-lg animate-fade-in text-left flex gap-3 ${
            ann.type === 'DANGER' ? 'bg-red-50 border-red-500 text-red-900' :
            ann.type === 'WARNING' ? 'bg-amber-50 border-amber-500 text-amber-900' :
            ann.type === 'SUCCESS' ? 'bg-green-50 border-green-500 text-green-900' :
            'bg-blue-50 border-blue-500 text-blue-900'
          }`}
        >
          <div className="shrink-0 mt-1">
            {ann.type === 'DANGER' || ann.type === 'WARNING' ? <AlertTriangle size={20} /> : 
             ann.type === 'SUCCESS' ? <CheckCircle size={20} /> : <Info size={20} />}
          </div>
          <div>
            <h4 className="font-black text-xs uppercase tracking-wider mb-1">{ann.title}</h4>
            <p className="text-sm font-medium leading-tight opacity-90">{ann.message}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

function LeagueApp() {
  const { user, team, isLoading, readOnly, login, logout, updateTeam } = useAuth();
  const [view, setView] = useState('dashboard');

  // v6.11.50 — navegación con contexto: el modal de equipo puede pedir "llevame a
  // Nuevo Partido con ESTE rival ya elegido". El id vive acá arriba porque el
  // salto cruza tres componentes (modal → Fixture → ScheduleMatch).
  const [preselectOpponentId, setPreselectOpponentId] = useState<string | null>(null);
  const navigate = useCallback<NavigateFn>((nextView: string, opts?: NavigateOptions) => {
    if (opts?.opponentTeamId) setPreselectOpponentId(opts.opponentTeamId);
    if (opts?.highlightMatchId) setMatchIdToHighlight(opts.highlightMatchId);
    if (opts?.eventId) setEventIdToOpen(opts.eventId);
    setView(nextView);
  }, []);

  // v6.11.74 — Simulacro: acá arriba y no cerca del render, porque más abajo hay
  // early returns por ruta (preview, sponsor, juez de cancha…) y un hook después de un
  // early return cambia de orden entre renders. Devuelve sólo el hecho del
  // servidor —si esta persona ya lo hizo—; las condiciones de pantalla se
  // resuelven donde se pinta la tarjeta.
  const simulacroGate = useSimulacroGate({
    sessionToken: team?.status === 'ACTIVO' ? getCurrentSession()?.token : null,
  });

  const [substitutionToken, setSubstitutionToken] = useState<string | null>(null);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [leagueSlug, setLeagueSlug] = useState<string | null>(null);
  // v6.11.28.02 — Liga resuelta por el arranque (`?league=`, única 'enabled', o
  // única con inscripción abierta). Alimenta el fetch de settings del visitante
  // SIN sesión; el participante logueado usa la liga de su equipo. Ver el efecto
  // de resolución de settings más abajo.
  const [bootLeagueId, setBootLeagueId] = useState<string | null>(null);
  // Bump para forzar un reintento del fetch de settings (foco/conexión de vuelta,
  // o el botón "Reintentar" del splash).
  const [settingsRetryTick, setSettingsRetryTick] = useState(0);
  // True cuando el splash de arranque lleva demasiado tiempo sin poder resolver:
  // pasa de spinner mudo a mensaje + reintento.
  const [bootStalled, setBootStalled] = useState(false);
  // Cantidad de ligas con inscripción abierta cuando NO se pudo resolver una
  // liga de entrada única (0 o >=2 'enabled'). Se usa en el gate de DNI para,
  // ante un DNI nuevo sin liga elegida, mandar al selector en vez de decir
  // "inscripciones cerradas" (multi-liga).
  const [openRegistrationCount, setOpenRegistrationCount] = useState(0);
  const [matchIdToEdit, setMatchIdToEdit] = useState<string | null>(null);
  // v6.11.91 — Partido a RESALTAR en Mi Agenda (no abre nada). Lo pide la
  // notificación de programación: "el rival confirmó el partido" señala la
  // tarjeta, y la carga de resultado sigue su propia ventana.
  const [matchIdToHighlight, setMatchIdToHighlight] = useState<string | null>(null);
  // v6.11.225 — Evento intermedio abierto en la vista `league-event`.
  const [eventIdToOpen, setEventIdToOpen] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<any[]>([]); // Moved here for AnnouncementsCarousel
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [registrationAllowedByJoin, setRegistrationAllowedByJoin] = useState(false);
  const [dismissedInAppWarning, setDismissedInAppWarning] = useState(false);
  // v6.10.0.29: jugador de una edición finalizada que eligió inscribirse en la nueva
  const [enrollNewEdition, setEnrollNewEdition] = useState(false);
  // v6.11.4 (Fase 9): hub multi-liga al ingresar por DNI
  const [entryOptions, setEntryOptions] = useState<PlayerEntryOptions | null>(null);
  const [entryDni, setEntryDni] = useState<number | null>(null);
  const [enrollLeagueSlug, setEnrollLeagueSlug] = useState<string | null>(null);
  // Reconciliación de deep-link de liga para un usuario YA logueado (sesión de
  // otra edición). Cuando alguien con sesión activa abre el link de inscripción de
  // otra liga (`/?login=true&league=<slug>`, típico por WhatsApp), sin esto caía al
  // dashboard de su edición vieja porque el "foco por liga" sólo vivía en el gate de
  // DNI. `reconcilingLeague` muestra un loader mientras se resuelve (evita el flash
  // del dashboard viejo); `leagueClosedNotice` guarda el nombre de la liga cuando la
  // inscripción está cerrada y el jugador no participa de ella.
  const [reconcilingLeague, setReconcilingLeague] = useState(false);
  const [leagueClosedNotice, setLeagueClosedNotice] = useState<string | null>(null);
  // v6.11.318 — "Reemplazado" es por edición y se calcula en el backend
  // (get_player_entry_options.replaced_leagues). La pantalla de reemplazo se
  // muestra sólo a quien fue reemplazado en alguna edición y no tiene pareja ni
  // inscripción abierta en ninguna otra; el que juega en otra entra a esa.
  const [replacedInfo, setReplacedInfo] = useState<ReplacedLeagueOption | null>(null);
  const deepLinkReconciledRef = useRef(false);

  // Entornos de desarrollo/testing donde se saltean los gates de dispositivo
  // (mobile-only desktop / PWA obligatoria / in-app browser):
  //  - localhost / 127.0.0.1 y cualquier dev server (`import.meta.env.DEV`, incluye
  //    acceso por IP de LAN desde un celular real durante `npm run dev`).
  //  - dominios `*.vercel.app` (previews y aliases de Vercel) → permite revisar un
  //    deploy de preview desde el navegador del celular sin instalar la PWA.
  // PRODUCCIÓN real corre en el dominio custom `liga.ajupam.ar` (NO termina en
  // .vercel.app y DEV es false), así que ahí los gates siguen 100% activos.
  const isLocalhost = window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || import.meta.env.DEV
    || window.location.hostname.endsWith('.vercel.app');

  // Login/Form states
  // ?login=true (p.ej. desde el CTA "Inscribir" de la landing) abre directo el
  // ingreso por DNI, sin pasar por el splash intermedio.
  const [loginMode, setLoginMode] = useState(
    () => new URLSearchParams(window.location.search).get('login') === 'true'
  );
  const [registerMode, setRegisterMode] = useState(false);
  const [dniInput, setDniInput] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  // UX: un DNI que YA está en una liga activa, entrando desde el navegador móvil
  // (no PWA), debe usar la app instalada — no seguir la inscripción/hub/dashboard
  // en el navegador. Se resuelve en el gate de DNI (handleLogin) y bloquea sin
  // escape (BrowserBlockScreen). Los que solo tienen ediciones finalizadas (o son
  // nuevos) NO activan esto → siguen el flujo de inscripción normal en el navegador.
  const [mustUseApp, setMustUseApp] = useState(false);

  // Playoffs: bracket visible cuando bracket_published_at <= NOW()
  const [isBracketPublished, setIsBracketPublished] = useState(false);

  // Admin/Venue authentication states
  const [adminSessionToken, setAdminSessionToken] = useState<string | null>(null);
  const [adminRole, setAdminRole] = useState<'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN' | null>(null);
  // v6.11.x: userId de la sesión admin ya validada. Se pasa a AdminDashboard para
  // que NO vuelva a llamar get_admin_session_info (loader redundante) y arranque
  // el fetch del perfil directamente, en paralelo con la carga de ligas.
  const [adminUserId, setAdminUserId] = useState<string | null>(null);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showVenueRedirect, setShowVenueRedirect] = useState(false);
  const [validatingAdminSession, setValidatingAdminSession] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // El admin de predios ahora vive nativo en platform-ajupam
        // (predios.ajupam.ar/admin). Cualquier hit a la URL legacy
        // /admin-predios muestra la pantalla informativa con auto-redirect a
        // los 5s, en lugar de saltar mudo al otro dominio.
        // Doc: platform-ajupam/docs/liga-redirect-venue-admins.md
        if (window.location.pathname.startsWith('/admin-predios')) {
          setShowVenueRedirect(true);
          return;
        }

        // Ruteo por URL. v6.11.66: pasó a ser lo PRIMERO del arranque. Antes
        // colgaba al final de este `init`, detrás de la resolución de liga, los
        // settings y los anuncios: quien tocaba una notificación push veía el
        // Inicio durante esos round-trips y recién después saltaba a la pantalla
        // correcta. Es ruteo puro (lee el pathname, nada más), así que no tiene
        // por qué esperar a la red.
        const path = window.location.pathname;
        if (path.startsWith('/join/')) {
          const code = path.split('/').pop();
          if (code) {
            setJoinCode(code.toUpperCase());
            setView('registration');
          }
        } else if (path.startsWith('/suplencia/')) {
          const token = path.split('/')[2];
          if (token) {
            setSubstitutionToken(token);
            setView('substitute_registration');
          }
        } else if (path.startsWith('/liga') || isRootLevelLeagueSlug(path)) {
          // Rutas públicas de liga — manejadas por PublicRouter en render.
          // v6.11.15.00: incluye también root-level slug (`/liga-ajupam-5ra`).
          // No setear view, el render las detecta por pathname
        } else if (path.startsWith('/partidos-liga')) {
          setView('public_matches');
        } else if (path.startsWith('/admin-liga')) { // Nueva ruta para admin
          setShowAdminLogin(true);
        // /admin-predios → redirect permanente arriba (predios.ajupam.ar/admin)
        } else {
          // Deep links de notificaciones (push / WhatsApp). El mapa path → vista
          // es el mismo que usa la campanita: utils/notificationTargets.
          const target = resolveDeepLinkPath(path);
          if (target) {
            setView(target.view);
            if (target.matchId) setMatchIdToEdit(target.matchId);
            if (target.highlightMatchId) setMatchIdToHighlight(target.highlightMatchId);
            if (target.eventId) {
              setEventIdToOpen(target.eventId);
              // v6.11.225 — El link del evento se comparte por WhatsApp: el que
              // lo abre sin sesión pasa por el ingreso por DNI, y el ingreso
              // termina siempre en el Inicio. El destino se guarda acá y lo
              // consume `goHomeAfterLogin`. Con sesión no hace falta: la vista
              // ya quedó en el evento. (Este `init` puede volver a correr después
              // del ingreso —el Suspense raíz re-monta App—; sin el chequeo de
              // sesión re-guardaría el pendiente que el ingreso acaba de consumir.)
              if (!getCurrentSession()) {
                rememberPendingEvent(target.eventId);
                setLoginMode(true);
              }
            }
          }
        }

        // v6.7.21: en multi-liga el RPC get_app_settings_v2(NULL) hace RAISE
        // si hay >=2 ligas 'enabled'. Para evitarlo, resolvemos primero la
        // liga (única enabled o null cuando hay >=2) y pasamos su id explícito.
        //
        // Resolución de la liga de ENTRADA (settings + inscripción):
        //   1. ?league=<slug> en la URL → la liga elegida en el selector de la
        //      landing multi-liga (el link de "Inscribirme" lo agrega). Esto
        //      permite anotarse con >=2 ligas 'enabled' sin que settings quede
        //      null y sin caer en "inscripciones cerradas".
        //   2. getCurrentLeague() → única 'enabled' (null si hay 0 o >=2).
        //   3. Si sigue null, contamos las ligas con inscripción abierta: si hay
        //      exactamente 1, la usamos; si hay >=2 dejamos la liga sin resolver
        //      (el gate de DNI manda al selector ante un DNI nuevo). NO redirigimos
        //      acá: `/?login=true` también lo usa el participante que YA existe
        //      para loguearse (se resuelve por DNI, sin liga), y un redirect lo
        //      dejaría sin poder entrar.
        const params = new URLSearchParams(window.location.search);
        const leagueParam = params.get('league');
        let currentLeague = leagueParam
          ? await getLeagueBySlug(leagueParam).catch(() => null)
          : null;
        if (!currentLeague) {
          currentLeague = await getCurrentLeague().catch(() => null);
        }
        if (!currentLeague) {
          const opens = await getPublicPreRegistrationLeagues().catch(() => []);
          setOpenRegistrationCount(opens.length);
          if (opens.length === 1) {
            currentLeague = await getLeagueBySlug(opens[0].slug).catch(() => null);
          }
        }
        setLeagueSlug(currentLeague?.slug ?? null);
        // v6.11.28.02 — El fetch de settings se movió al efecto resiliente de abajo
        // (`settingsLeagueId`), que reintenta con backoff. Acá sólo publicamos qué
        // liga resolvió el arranque; en producción (>=2 ligas 'enabled')
        // getCurrentLeague() devuelve null y la liga la aporta el equipo del jugador.
        setBootLeagueId(currentLeague?.id ?? null);

        // Cargar diccionarios de validación de nombres desde DB
        loadDictionariesFromDB().catch((error) => {
          console.warn('Error cargando diccionarios, usando hardcodeados:', error);
        });

        // Fetch announcements for carousel — SCOPEADO a la liga actual (multi-liga).
        // Sin scope, el carrusel pre-login mostraba anuncios de ediciones archivadas
        // (p.ej. los de la 4ta Ed en la pantalla de la 5ta). Incluimos los
        // "generales" (league_id NULL) para anuncios cross-liga si el admin los crea.
        let annQuery = supabase
          .from('announcements')
          .select('*')
          .eq('is_active', true);
        if (currentLeague?.id) {
          annQuery = annQuery.or(`league_id.eq.${currentLeague.id},league_id.is.null`);
        } else {
          annQuery = annQuery.is('league_id', null);
        }
        const { data } = await annQuery.order('created_at', { ascending: false });
        if (data) setAnnouncements(data);

      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    init();
  }, []);

  // Reconciliación de deep-link de liga para usuarios YA logueados.
  // Escenario: alguien con sesión activa de una edición (p.ej. la 4ta, en curso)
  // abre el link de inscripción de OTRA liga (la 5ta) → `?league=<slug>`. El "foco
  // por liga" sólo existía en el gate de DNI (handleLogin), que este usuario no
  // atraviesa, así que caía al dashboard de su edición vieja. Acá lo reconciliamos:
  //   - Ya inscripto en esa liga  → cambiamos la sesión a esa edición (dashboard).
  //   - No inscripto + inscripción ABIERTA → lo llevamos a inscribirse en esa liga.
  //   - No inscripto + inscripción CERRADA → aviso "inscripción cerrada".
  // Sólo aplica a sesiones ya persistidas (`!entryDni`): el flujo de DNI ya rutea
  // por su cuenta. El start_url de la PWA es `/` (sin `?league=`), así que esto NO
  // se dispara en aperturas normales, sólo al seguir un link de liga.
  // v6.11.318 — Jugador logueado SIN equipo: ¿fue reemplazado en alguna edición y
  // no juega en ninguna otra? Entonces ve la pantalla de reemplazo (con el equipo
  // del que salió). Cubre el ingreso por DNI (cae acá como "huérfano") y la sesión
  // que se restaura después de que aprobaron su reemplazo. Si el RPC falla, no se
  // afirma nada: sigue el flujo de siempre.
  useEffect(() => {
    if (!user || team || isLoading) { setReplacedInfo(null); return; }
    let cancelled = false;
    getPlayerEntryOptions(user.dni)
      .then((opts) => {
        if (cancelled || !opts) return;
        const replaced = opts.replaced_leagues ?? [];
        // Con una inscripción abierta en otra edición no se lo frena acá: el
        // flujo normal le ofrece anotarse (esa edición ya excluye las que lo
        // reemplazaron).
        const nothingElse = opts.leagues.length === 0 && opts.open_registrations.length === 0;
        setReplacedInfo(nothingElse && replaced.length > 0 ? replaced[0] : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, team, isLoading]);

  useEffect(() => {
    if (!user || entryDni || deepLinkReconciledRef.current) return;
    const leagueParam = new URLSearchParams(window.location.search).get('league');
    if (!leagueParam) return;
    const currentLeagueSlug = (team as any)?.league?.slug ?? null;
    if (currentLeagueSlug === leagueParam) { deepLinkReconciledRef.current = true; return; }

    deepLinkReconciledRef.current = true;
    setReconcilingLeague(true);
    (async () => {
      try {
        const opts = await getPlayerEntryOptions(user.dni).catch(() => null);
        // Error de red al resolver opciones: no bloqueamos ni mostramos "cerrada"
        // (sería un falso negativo) → cae a su edición actual (dashboard).
        if (!opts) return;
        const owned = opts.leagues.find((l) => l.slug === leagueParam);
        if (owned) {
          // Ya participa de esa liga → cambiar de edición (misma mecánica que el hub).
          await login(user.dni, owned.team_id);
          setView('dashboard');
          return;
        }
        const open = opts.open_registrations.find((o) => o.slug === leagueParam);
        if (open) {
          // No participa pero la inscripción está abierta → al formulario de esa liga.
          setEnrollLeagueSlug(open.slug);
          setEnrollNewEdition(true);
          return;
        }
        // No participa y la inscripción está cerrada → aviso claro.
        const l = await getLeagueBySlug(leagueParam).catch(() => null);
        setLeagueClosedNotice(l?.name ?? 'esta liga');
      } finally {
        setReconcilingLeague(false);
        // Limpiar `?league=`/`?login=` de la URL para no re-disparar en un refresh.
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete('league');
          u.searchParams.delete('login');
          window.history.replaceState({}, '', u.pathname + u.search + u.hash);
        } catch { /* URL API no disponible */ }
      }
    })();
  }, [user, team, entryDni, login]);

  // QW1 — Instrumentación por pantalla. La navegación de participante es por
  // estado (`setView`), no por URL, así que sin esto GA4/Clarity ven todo bajo
  // "/" (~92% del tráfico). Emitimos un virtual pageview "/app/<pantalla>" por
  // cada cambio de view (solo con usuario logueado dentro del shell de la app).
  useEffect(() => {
    if (!user) return;
    trackAppView(view);
  }, [view, user]);

  // v6.11.225 — La URL `/evento/<id>` vale mientras se mira ese evento. Si el
  // jugador sale por otro lado (nav inferior, campanita), la URL vuelve a "/":
  // si no, recargar la app reabriría un evento que ya cerró. Sólo actúa cuando
  // ya se abrió un evento (`eventIdToOpen`), así no pisa el arranque por link.
  useEffect(() => {
    if (view === 'league-event' || !eventIdToOpen) return;
    setEventIdToOpen(null);
    if (window.location.pathname.startsWith('/evento/')) {
      window.history.replaceState({}, '', '/');
    }
  }, [view, eventIdToOpen]);

  // QW1 — Tags de contexto para segmentar dead-clicks / recordings / embudos
  // por categoría, estado de equipo, fase de temporada y PWA vs navegador.
  useEffect(() => {
    if (!user) return;
    const seasonPhase = isBracketPublished
      ? 'playoffs'
      : settings?.is_season_active
        ? 'regular'
        : 'preseason';
    setAppContext({
      category: (team as any)?.category_name ?? null,
      teamStatus: team?.status ?? null,
      seasonPhase,
      isPwa: isInstalledPWA(),
      userId: user.id,
    });
  }, [user, team?.id, team?.status, isBracketPublished, settings?.is_season_active]);

  // Validar sesión de admin/venue al cargar (SOLO en rutas de admin)
  useEffect(() => {
    const validateSession = async () => {
      const path = window.location.pathname;
      const isAdminRoute = path.startsWith('/admin-liga') || path.startsWith('/admin-predios');

      // Solo validar sesión de admin si estamos en ruta de admin
      if (!isAdminRoute) {
        // Si NO estamos en ruta admin, limpiar cualquier estado admin residual
        setAdminSessionToken(null);
        setAdminRole(null);
        setAdminUserId(null);
        return;
      }

      const storedToken = localStorage.getItem('admin_session_token');
      if (!storedToken) return;

      setValidatingAdminSession(true);
      try {
        const sessionInfo = await validateAdminSession(storedToken);
        if (sessionInfo) {
          // Sesiones VENUE_ADMIN remanentes: ya no operan en Liga (la gestión
          // se mudó a predios.ajupam.ar/admin). Limpiamos token + mostramos
          // pantalla de redirect en lugar del VenueDashboard legacy.
          if (sessionInfo.role === 'VENUE_ADMIN') {
            await logoutAdmin(storedToken).catch(() => undefined);
            localStorage.removeItem('admin_session_token');
            setAdminSessionToken(null);
            setAdminRole(null);
            setShowVenueRedirect(true);
            return;
          }
          setAdminSessionToken(storedToken);
          setAdminRole(sessionInfo.role);
          setAdminUserId(sessionInfo.userId);
        } else {
          // Sesión inválida o expirada
          localStorage.removeItem('admin_session_token');
          setAdminSessionToken(null);
          setAdminRole(null);
          setAdminUserId(null);
        }
      } catch (error) {
        console.error('Error validating admin session:', error);
        localStorage.removeItem('admin_session_token');
        setAdminSessionToken(null);
        setAdminRole(null);
        setAdminUserId(null);
      } finally {
        setValidatingAdminSession(false);
      }
    };

    validateSession();
  }, []);

  // Cargar estado de bracket cuando el equipo está disponible
  useEffect(() => {
    if (!team?.category_id) return;
    let cancelled = false;
    (async () => {
      // Supabase resuelve con { data, error } (no rechaza) → chequear error/null.
      // Cast: los tipos generados no incluyen playoff_bracket_config (→ data: never).
      const { data, error } = await supabase
        .from('playoff_bracket_config')
        .select('bracket_published_at')
        .eq('category_id', team.category_id)
        .single() as { data: { bracket_published_at: string | null } | null; error: unknown };
      if (cancelled) return;
      if (error || !data) {
        setIsBracketPublished(false);
        return;
      }
      const publishedAt = data.bracket_published_at;
      setIsBracketPublished(!!publishedAt && new Date(publishedAt) <= new Date());
    })();
    return () => { cancelled = true; };
  }, [team?.category_id]);

  // ==========================================================================
  // Resolución de `settings` — única y con reintentos (v6.11.28.02)
  // ==========================================================================
  // La liga de la que se leen los settings, por orden de precedencia:
  //   1. la del EQUIPO del participante logueado (la correcta para él, y la
  //      única disponible en multi-liga), y
  //   2. la que resolvió el arranque (`?league=`, única 'enabled', o única con
  //      inscripción abierta) para el visitante sin sesión.
  //
  // v6.11.4 — arrancó como fix del modo solo-lectura (edición finalizada vista
  // vía hub). Ahora aplica SIEMPRE que haya un equipo: en multi-liga (>=2
  // 'enabled') getCurrentLeague() del boot devuelve null y `settings` queda
  // null → el participante quedaba trabado en el loader de arranque. Cargando
  // los settings desde su propia liga se destraba y además son los correctos.
  //
  // v6.11.28.02 — BUG DE FONDO: el fetch era un DISPARO ÚNICO con
  // `.catch(() => {})`. En producción hay 3 ligas 'enabled' (Gran Mendoza 5ta,
  // Sur, Valle de Uco) → getCurrentLeague() devuelve null y la liga del equipo es
  // la ÚNICA fuente de `settings`; y el splash de arranque no se levanta hasta
  // tenerlos (guard de abajo). Entonces UN solo fallo de esa llamada — timeout de
  // 15s, 4G intermitente, o el fetch que quedó congelado al reanudar la PWA desde
  // segundo plano — dejaba `settings` en null PARA SIEMPRE, sin reintento: la app
  // se quedaba en el loader y sólo salía recargando a mano (síntoma reportado).
  // Golpeaba sobre todo al que venía de la 4ta y ahora está en la 5ta porque su
  // sesión persistida trae el equipo VIEJO: al arrancar dispara esta llamada dos
  // veces (liga de la 4ta y, tras el refresco en segundo plano, la de la 5ta), y
  // su boot depende de una cadena de red más larga.
  // Ahora: reintentos con backoff + reintento al recuperar foco/conexión.
  // Liga que el participante está VIENDO (la de su equipo). Extraída a variable
  // para poder usarla como dependencia estática de los efectos de abajo.
  const viewedLeagueId = ((team as any)?.league_id as string | undefined) ?? null;
  const settingsLeagueId = viewedLeagueId ?? bootLeagueId;

  useEffect(() => {
    if (!settingsLeagueId) return;
    let cancelled = false;

    (async () => {
      // 0s → 2s → 5s → 10s → 20s → 30s. Cubre de sobra un bache de red móvil; si
      // al final sigue fallando, el watchdog del splash ofrece "Reintentar".
      const delays = [0, 2000, 5000, 10000, 20000, 30000];
      for (const delay of delays) {
        if (cancelled) return;
        if (delay) await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        try {
          const s = await getAppSettings(settingsLeagueId);
          if (cancelled || !s) continue;
          setSettings(s);
          // Cachear el logo de la liga para brandear el loader de arranque del
          // próximo ingreso (en boot, settings aún no cargó → se lee de aquí).
          try {
            const logo = (s as any).league_logo_url;
            const treat = (s as any).league_logo_treatment;
            if (logo) localStorage.setItem('cached_league_logo_url', logo);
            else localStorage.removeItem('cached_league_logo_url');
            if (treat) localStorage.setItem('cached_league_logo_treatment', treat);
            else localStorage.removeItem('cached_league_logo_treatment');
          } catch { /* localStorage no disponible */ }
          return;
        } catch (e) {
          console.warn('[settings] fallo al cargar la liga, reintentando:', e);
        }
      }
      console.error('[settings] no se pudieron cargar tras todos los reintentos');
    })();

    return () => { cancelled = true; };
    // `readOnly` se mantiene como dep: al cambiar de edición hay que releer.
  }, [readOnly, settingsLeagueId, settingsRetryTick]);

  // Reintento al recuperar foco o conexión, SÓLO si todavía no hay settings. Es
  // el caso típico de la PWA que estuvo en segundo plano: el fetch en vuelo quedó
  // congelado y nunca settlea, así que al volver hay que rearrancarlo.
  useEffect(() => {
    if (settings) return;
    const retry = () => {
      if (document.visibilityState === 'visible') setSettingsRetryTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', retry);
    window.addEventListener('online', retry);
    return () => {
      document.removeEventListener('visibilitychange', retry);
      window.removeEventListener('online', retry);
    };
  }, [settings]);

  // v6.11.43 — Los settings YA cargados también se releen al volver del segundo
  // plano. El efecto de arriba sólo cubría el caso "nunca cargaron"; una vez
  // resueltos quedaban congelados para toda la vida de la pestaña. Eso hacía que
  // el arranque de la temporada (`is_season_active`), las fechas de fase y los
  // flags de features no llegaran nunca a la app que quedó abierta: había que
  // recargar a mano. AuthProvider hace lo propio con la sesión y el equipo.
  useAppResume(() => setSettingsRetryTick((t) => t + 1), {
    enabled: !!settings,
    throttleMs: 60_000,
    intervalMs: 5 * 60_000,
  });

  // Aviso del arranque de temporada: si `is_season_active` pasa de false a true
  // con la app abierta, el participante ve el cartel en vez de descubrirlo por
  // casualidad. El primer valor sólo siembra la referencia (no se anuncia).
  const prevSeasonActiveRef = useRef<boolean | null>(null);
  const seasonActive = settings?.is_season_active;
  useEffect(() => {
    if (seasonActive === undefined) return; // settings todavía no cargaron
    const prev = prevSeasonActiveRef.current;
    prevSeasonActiveRef.current = seasonActive;
    if (prev === false && seasonActive) {
      emitLeagueDataUpdated({ message: '¡Arrancó la temporada!', reason: 'season-start' });
    }
  }, [seasonActive]);

  // ==========================================================================
  // Puente "tu edición terminó → anotate en la nueva" (v6.11.28.02)
  // ==========================================================================
  // `settings` está scopeado a la liga que el participante está VIENDO. Si esa
  // liga es su edición archivada, `is_registration_active` es false POR
  // DEFINICIÓN → la SeasonEndedScreen recibía `newEditionOpen={false}` y nunca le
  // ofrecía anotarse a la edición siguiente: el jugador de la 4ta quedaba en "tu
  // edición finalizó" sin ninguna salida. (En modo solo-lectura el CTA lo resuelve
  // NewEditionCta por su cuenta, vía get_player_entry_options; esta rama, la de las
  // sesiones sin `readOnly`, no tenía equivalente.)
  //
  // Regla "default por familia": una familia tiene como mucho UNA liga activa a la
  // vez, así que desde la edición vieja del jugador se puede resolver sin
  // ambigüedad cuál es "la nueva" y leer SUS settings para el CTA.
  // v6.11.62 — También el `slug`: sin él, el puente "inscribirme a la nueva
  // edición" mandaba a Registration sin liga y el formulario no podía cargar
  // ninguna categoría (con >= 2 ligas `enabled`, `getCurrentLeague()` es null).
  const [nextEdition, setNextEdition] = useState<{ name: string | null; slug: string | null; registrationOpen: boolean } | null>(null);

  const viewedLeagueFinished = !!((team as any)?.league?.editionFinished
    || (team as any)?.league?.status === 'archived');

  useEffect(() => {
    if (!viewedLeagueId || !viewedLeagueFinished) { setNextEdition(null); return; }
    let cancelled = false;
    (async () => {
      const active = await getCurrentLeagueByFamily({ referenceLeagueId: viewedLeagueId });
      // Sin liga activa en la familia (entre temporadas), o la activa ES la que
      // está viendo → no hay puente que ofrecer.
      if (cancelled || !active || active.id === viewedLeagueId) return;
      const s = await getAppSettings(active.id).catch(() => null);
      if (cancelled) return;
      setNextEdition({
        name: (s as any)?.season_name || active.name || null,
        slug: active.slug ?? null,
        registrationOpen: !!(s as any)?.is_registration_active,
      });
    })();
    return () => { cancelled = true; };
  }, [viewedLeagueId, viewedLeagueFinished]);

  // Watchdog del splash de arranque: si a los 15s seguimos sin poder arrancar,
  // dejamos de mostrar un spinner mudo y ofrecemos un reintento explícito. Antes
  // el único escape era recargar la página a mano.
  // 15s = el timeout interno de getAppSettings, así que cuando aparece el aviso el
  // primer intento ya falló de verdad (no se pisa con un arranque lento pero sano).
  useEffect(() => {
    if (settings && !isLoading) { setBootStalled(false); return; }
    const t = setTimeout(() => setBootStalled(true), 15000);
    return () => clearTimeout(t);
  }, [settings, isLoading, settingsRetryTick]);

  // v6.11.225 — Fin del ingreso por DNI: al Inicio, salvo que haya entrado por
  // el link de un evento (`/evento/<id>`), en cuyo caso vuelve al evento.
  const goHomeAfterLogin = () => {
    const pendingEventId = takePendingEvent();
    if (pendingEventId) {
      setEventIdToOpen(pendingEventId);
      setView('league-event');
      return;
    }
    setView('dashboard');
  };

  const handleLogin = async () => {
    if (!dniInput) return;

    // VALIDACIÓN ESTRICTA DE DNI (v4.1.2)
    const dniTrimmed = dniInput.trim();

    // Validar que solo contenga números
    if (!/^\d+$/.test(dniTrimmed)) {
      setLoginError('El DNI debe contener solo números.');
      return;
    }

    // Validar que tenga exactamente 8 dígitos
    if (dniTrimmed.length !== 8) {
      setLoginError('El DNI debe tener exactamente 8 dígitos.');
      return;
    }

    // Validar que el DNI sea mayor a 10.000.000 (v4.2.6)
    const dniNumber = parseInt(dniTrimmed, 10);
    if (dniNumber <= 10000000) {
      setLoginError('DNI inválido. Debe ser mayor a 10.000.000.');
      return;
    }

    setLoginError(null);
    setEntryDni(dniNumber);
    // Contexto de liga explícito: el jugador entró desde la landing de UNA liga
    // puntual (el CTA "Inscribir" agrega ?league=<slug>). En ese caso NO tiene
    // sentido preguntarle "elegí tu liga" — es obvio que quiere anotarse en esa.
    const urlLeagueSlug = new URLSearchParams(window.location.search).get('league');
    try {
      // v6.11.4 (Fase 9) — Hub multi-liga: si el jugador tiene más de una opción
      // (varias ediciones, o una finalizada + inscripción abierta), mostrar el hub
      // para que elija. Si hay una sola opción, entra directo. Fallback seguro: si
      // el RPC falla, login normal (comportamiento previo, sin regresión).
      const opts = await getPlayerEntryOptions(dniNumber).catch(() => null);
      if (opts && opts.profile_exists) {
        // UX — Participante en liga ACTIVA desde el navegador móvil (no PWA): tiene
        // que ingresar por la app instalada (la instaló al participar de una edición
        // previa). Se corta ANTES del hub/inscripción/dashboard y bloquea sin escape.
        // "Liga activa" = tiene equipo en alguna edición NO finalizada (misma
        // semántica que `inCourse` más abajo). Los que solo tienen ediciones
        // finalizadas o son nuevos NO entran acá → siguen la inscripción en el
        // navegador. En PWA / desktop / localhost el gate no aplica (desktop ya lo
        // bloquea MobileOnlyScreen; en la app el ingreso es el flujo normal).
        const enLigaActiva = opts.leagues.some((l) => !l.is_finished);
        if (enLigaActiva && isMobileDevice() && !isInstalledPWA() && !isLocalhost) {
          setMustUseApp(true);
          return;
        }
        // v6.11.17.00 — Foco por contexto de liga: si vino con ?league=<slug>
        // (desde la landing de esa liga), resolvemos DIRECTO esa liga y salteamos
        // el hub multi-liga. Evita el loop "DNI → elegí liga → landing → DNI":
        //   - Ya tiene equipo en esa liga → entra a su dashboard.
        //   - Tiene inscripción abierta ahí → va derecho a inscribirse.
        // Si la liga del contexto no aplica a este jugador, seguimos con el flujo
        // multi-liga normal (hub / opción única).
        if (urlLeagueSlug) {
          const ownedInUrlLeague = opts.leagues.find((l) => l.slug === urlLeagueSlug);
          if (ownedInUrlLeague) {
            await login(dniNumber, ownedInUrlLeague.team_id);
            goHomeAfterLogin();
            return;
          }
          const openInUrlLeague = opts.open_registrations.find((o) => o.slug === urlLeagueSlug);
          if (openInUrlLeague) {
            setEnrollLeagueSlug(openInUrlLeague.slug);
            setEntryOptions(null);
            try {
              await login(dniNumber); // cargar perfil para reusar datos en la inscripción
            } catch (_e) {
              // sin equipo: continuamos igual al flujo de registro
            }
            setEnrollNewEdition(true);
            return;
          }
        }
        // Foco en la liga en curso: si el jugador tiene UNA sola edición en curso
        // (aunque haya jugado ediciones pasadas) y no hay inscripción abierta en
        // otra liga, entra directo sin pasar por el hub. Las ediciones anteriores
        // quedan accesibles de forma discreta desde el Perfil.
        const inCourse = opts.leagues.filter((l) => !l.is_finished);
        if (inCourse.length === 1 && opts.open_registrations.length === 0) {
          await login(dniNumber, inCourse[0].team_id);
          goHomeAfterLogin();
          return;
        }
        const totalOptions = opts.leagues.length + opts.open_registrations.length;
        if (totalOptions > 1) {
          setEntryOptions(opts);
          return;
        }
        if (opts.leagues.length === 1) {
          await login(dniNumber, opts.leagues[0].team_id);
          goHomeAfterLogin();
          return;
        }
        // v6.11.12 — Perfil SIN equipo en ninguna edición pero con una inscripción
        // abierta: ir directo a inscribirse en esa liga en vez de quedar en el
        // dead-end "sin equipo asignado". (get_player_entry_options ya garantiza
        // que estas ligas tienen la inscripción activa.) Antes esto caía al
        // login sin equipo y no ofrecía ningún camino para anotarse.
        if (opts.open_registrations.length >= 1) {
          setEnrollLeagueSlug(opts.open_registrations[0].slug);
          setEntryOptions(null);
          try {
            await login(dniNumber); // cargar perfil para reusar datos en la inscripción
          } catch (_e) {
            // sin equipo: continuamos igual al flujo de registro
          }
          setEnrollNewEdition(true);
          return;
        }
        // 0 ligas y 0 inscripciones abiertas → login normal (huérfano)
      }
      await login(dniNumber);
      goHomeAfterLogin();
    } catch (e: any) {
      if (e.message === 'USER_NOT_FOUND') {
        if (settings?.is_registration_active) {
          setLoginMode(false);
        } else if (joinCode) {
          // Inscripciones cerradas pero tiene código de invitación → permitir unirse a equipo incompleto
          setRegistrationAllowedByJoin(true);
          setLoginMode(false);
        } else if (openRegistrationCount >= 1) {
          // Multi-liga: hay inscripción abierta pero sin una liga elegida (el
          // participante entró por "Ingresar", no por una liga puntual). Lo
          // mandamos al selector para que elija en cuál anotarse. (Cuando vino con
          // ?league=<slug> desde la landing de una liga, currentLeague ya resolvió
          // en el arranque y openRegistrationCount queda en 0 → esta rama no aplica
          // y el gate cae en la rama de inscripción activa de ESA liga.)
          window.location.href = '/';
        } else {
          setLoginError('Este DNI no está registrado. Las inscripciones están cerradas.');
        }
      } else if (e.message === 'USER_BLOCKED') {
        setLoginError('Tu cuenta está bloqueada.');
      } else if (e.message === 'USER_REPLACED') {
        setLoginError('Tu equipo realizó un cambio de jugador y ya no formás parte del mismo. Si tenés dudas, contactá a los administradores.');
      } else if (e.message === 'TEAM_DELETED') {
        setLoginError('Tu equipo fue dado de baja.');
      } else {
        setLoginError('Error al iniciar sesión.');
      }
    }
  };

  // v6.11.4 — desde el hub: entrar a una edición (activa → normal; finalizada → solo lectura)
  const handleEnterLeague = async (league: PlayerLeagueOption) => {
    if (!entryDni) return;
    setEntryOptions(null);
    try {
      await login(entryDni, league.team_id);
      goHomeAfterLogin();
    } catch (e: any) {
      setLoginError('No se pudo ingresar a la edición. Probá de nuevo.');
    }
  };

  // Cambiar a otra edición desde el Perfil (usa el DNI de la sesión actual, no el
  // del flujo de ingreso). Finalizada → solo lectura (lo resuelve login()).
  // v6.11.304: en curso → queda guardada como la liga que disputa (loginWithDNI).
  // Si falla, la vista actual sigue igual y Perfil muestra el error.
  const handleSwitchEdition = async (teamId: string) => {
    if (!user?.dni) return;
    await login(user.dni, teamId);
    setView('dashboard');
  };

  // v6.11.4 — desde el hub: inscribirse en una edición con inscripción abierta
  const handleRegisterOpen = async (open: OpenRegistrationOption) => {
    if (!entryDni) return;
    setEnrollLeagueSlug(open.slug);
    setEntryOptions(null);
    try {
      await login(entryDni); // cargar perfil para reusar datos en la inscripción
    } catch (e: any) {
      // si no tiene equipo, igual continuamos al registro
    }
    setEnrollNewEdition(true);
  };

  const handleLogout = async () => {
    const path = window.location.pathname;
    const isAdminRoute = path.startsWith('/admin-liga') || path.startsWith('/admin-predios');

    // Si estamos en ruta admin, solo hacer logout de admin
    if (isAdminRoute && adminSessionToken) {
      try {
        await logoutAdmin(adminSessionToken);
      } catch (error) {
        console.error('Error logging out admin:', error);
        // Continuar con la limpieza local incluso si el RPC falla
      } finally {
        // Siempre limpiar sesión local y recargar
        localStorage.removeItem('admin_session_token');
        setAdminSessionToken(null);
        setAdminRole(null);
        setAdminUserId(null);
        // Recargar la página para volver al login
        window.location.href = '/admin-liga';
        return; // NO hacer logout de usuario participante
      }
    }

    // Si NO estamos en ruta admin, hacer logout de usuario participante
    takePendingEvent(); // v6.11.225 — un link de evento no sobrevive a un cambio de persona
    await logout();
    resetAppUser(); // limpia la identidad de PostHog (dispositivos compartidos)
    setDniInput('');
    setLoginMode(false);
    setLoginError(null);
    setShowAdminLogin(false);
    // Limpiar cualquier rastro de registro pendiente
    try { sessionStorage.removeItem('liga_ajupam_registration_form'); } catch (e) {}
  };

  const handleAdminLoginSuccess = (sessionToken: string, userId: string, role: 'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN', venueId: string | null) => {
    // VENUE_ADMIN ya no opera en Liga: la sesión recién creada se descarta y
    // el usuario va a loguearse de nuevo en predios.ajupam.ar/admin (la cookie
    // de Liga no sirve allá; son sistemas distintos con bcrypt importado).
    // Doc: platform-ajupam/docs/liga-redirect-venue-admins.md
    if (role === 'VENUE_ADMIN') {
      logoutAdmin(sessionToken).catch((err) => {
        console.warn('Error invalidando sesión VENUE_ADMIN antes de redirect:', err);
      });
      setShowAdminLogin(false);
      setShowVenueRedirect(true);
      return;
    }
    localStorage.setItem('admin_session_token', sessionToken);
    setAdminSessionToken(sessionToken);
    setAdminRole(role);
    setShowAdminLogin(false);
    window.history.pushState({}, '', '/admin-liga');
  };

  // v6.10.0.28: cuando no hay ninguna liga 'enabled', getCurrentLeague() devuelve
  // null y `settings` queda null. Antes el guard `!settings` dejaba al público
  // en spinner infinito. Estas rutas NO dependen de `settings` (público + paneles
  // con auth/datos propios), así que deben renderizar igual y degradar con su
  // propia UI (la home pública muestra la landing "entre temporadas").
  const guardPath = window.location.pathname;
  const settingsIndependentRoute =
    guardPath.startsWith('/liga') ||
    isRootLevelLeagueSlug(guardPath) || // v6.11.15.00 — root-slug landings
    guardPath.startsWith('/preview/') ||
    guardPath.startsWith('/sponsor/') ||
    guardPath.startsWith('/abridor') ||
    guardPath.startsWith('/juez') ||
    guardPath.startsWith('/recepcion') ||
    // v6.11.188 — /verificacion (control de acceso) tiene su propia auth y
    // resuelve la liga desde la sesión del staff: no depende de `settings`.
    // Sin esta excepción, con 0 o >=2 ligas 'enabled' queda en splash infinito.
    guardPath.startsWith('/verificacion') ||
    // v6.11.344 — /patrocinadores (encargado de sponsors): auth propia, la liga
    // sale de la sesión del staff. Mismo motivo que /verificacion.
    guardPath.startsWith('/patrocinadores') ||
    // v6.11.188 — /v/:token resuelve el equipo desde el token; tampoco depende
    // de `settings`. Es la ruta a la que cae un escaneo casual de la credencial.
    guardPath.startsWith('/v/') ||
    // v6.11.372 — /sorteo/<código> (QR del sorteo en sede): la liga sale del código del sorteo, no de `settings`.
    guardPath.startsWith('/sorteo/') ||
    guardPath.startsWith('/admin-predios') ||
    // v6.11.17.02 — /admin-liga NO depende de `settings`: el login y el
    // dashboard de admin tienen su propia auth (adminSessionToken) y cargan su
    // liga vía LeagueProvider. Sin esta excepción, cuando hay 0 o >=2 ligas
    // 'enabled' getCurrentLeague() devuelve null, `settings` queda null y el
    // guard `!settings` dejaba al admin colgado en el loader de arranque
    // (síntoma reportado en desktop, que es por donde entra el admin).
    guardPath.startsWith('/admin-liga') ||
    // Links de invitación (`/join/<code>`) y de suplencia (`/suplencia/<token>`)
    // NO dependen de `settings`: la liga se resuelve por el código/token
    // (Registration usa prefilledInviteCode; SubstituteRegistration se auto-scopea
    // por el token). Con 0 o >=2 ligas 'enabled' (o entre temporadas) `settings`
    // queda null y, sin esta excepción, quien abría el link quedaba en el splash
    // infinito (reportado: /join/<code> "se queda pensando"). Aplica también a quien
    // YA tiene equipo en OTRA edición: el link de invitación lo rutea al flujo de
    // "unirse al equipo" (shouldShowRegistration por joinCode), NO a su dashboard, así
    // que no necesita `settings` y no debe quedar atrapado esperándolo.
    guardPath.startsWith('/join/') ||
    // v6.11.225 — El link de un evento (`/evento/<id>`) abierto SIN sesión va
    // directo al ingreso por DNI, que no depende de `settings` (igual que "/").
    // Con sesión, el evento se muestra dentro del shell y espera los settings de
    // la liga del equipo como cualquier otra pantalla.
    (guardPath.startsWith('/evento/') && !user) ||
    guardPath.startsWith('/suplencia/') ||
    // Cualquier visitante SIN usuario en "/" (browser o PWA instalada, con o sin
    // ?login=true): el ingreso por DNI y la landing "entre temporadas" no dependen
    // de `settings` (lo consumen opcional con `?.`), así que no deben esperar a que
    // cargue. Un usuario LOGUEADO en "/" SÍ espera settings (el dashboard accede a
    // settings.* y crashearía con null); esos se cargan de la liga de su equipo. El
    // guard `!user` ya cubre ese crash.
    // v6.11.18.03 — antes exigía `!isInstalledPWA()`, lo que dejaba a un usuario NO
    // logueado dentro de la PWA instalada (start_url `/`, sin ?login) trabado en el
    // loader: en multi-liga getCurrentLeague()=null → `settings` nunca carga y sin
    // sesión el efecto por-equipo tampoco la resuelve (caso típico de iOS: la PWA
    // standalone tiene storage separado del navegador donde se inscribió → arranca
    // sin sesión). Subsume el caso previo de `/?login=true`.
    (guardPath === '/' && !user) ||
    // v6.11.28.02 — Participante LOGUEADO del que no se puede sacar ninguna liga:
    // sin equipo en ninguna edición (p.ej. venía de la 4ta y todavía no completó la
    // inscripción a la 5ta) y sin liga de arranque, porque con >=2 ligas 'enabled'
    // getCurrentLeague() devuelve null. Para él `settings` NO va a llegar nunca, así
    // que esperarlo era un splash infinito que ni recargando se destrababa. Se exige
    // `!isLoading` para no adelantarse al equipo que aún viene de la sesión
    // persistida. Es seguro: todas las lecturas de `settings` de acá para abajo son
    // opcionales (`settings?.`), y si más tarde aparece un equipo, el efecto de
    // settings lo resuelve y la pantalla se actualiza sola.
    (guardPath === '/' && !isLoading && !settingsLeagueId);

  // v6.11.208 — Los paneles del staff (juez de cancha, mesa, verificación) tienen su
  // propia sesión: no esperan a que cargue la del jugador. Si el celular del
  // juez de cancha también tiene su sesión de jugador, antes el panel quedaba en el
  // splash hasta que terminara de cargar su equipo (hasta 15 s con mala señal).
  const isStaffPanelRoute =
    guardPath.startsWith('/abridor') ||
    guardPath.startsWith('/juez') ||
    guardPath.startsWith('/recepcion') ||
    guardPath.startsWith('/verificacion') ||
    guardPath.startsWith('/patrocinadores');

  if ((isLoading && !isStaffPanelRoute) || validatingAdminSession || (!settings && !settingsIndependentRoute)) {
    // Loader de arranque brandeado: logo de la liga (de settings si ya cargó; si
    // no, del cache del último ingreso; si no, el logo por defecto de la app) con
    // un pulso suave en loop + un ring sutil. Tratamiento 'dark' (fondo azul).
    const bootLogo = (settings as any)?.league_logo_url
      || localStorage.getItem('cached_league_logo_url')
      || DEFAULT_LIGA_LOGO;
    const bootTreatment = (settings as any)?.league_logo_treatment
      || localStorage.getItem('cached_league_logo_treatment')
      || undefined;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-primary px-8">
        <img
          src={bootLogo}
          alt="Liga"
          className="h-28 w-auto max-w-[60%] object-contain animate-pulse-gentle"
          style={{ filter: getLogoFilter(bootTreatment, 'dark') || undefined }}
        />
        {!bootStalled ? (
          <div className="animate-spin h-7 w-7 border-2 border-white/30 border-t-white rounded-full" />
        ) : (
          // v6.11.28.02 — El arranque se pasó del watchdog. En vez de dejar al
          // usuario mirando un spinner infinito (y que la única salida sea recargar
          // a mano), le decimos qué pasa y le damos el reintento a mano. El
          // reintento automático con backoff sigue corriendo en paralelo.
          <div className="flex flex-col items-center gap-4 text-center max-w-xs">
            <p className="text-white/80 text-sm font-medium leading-relaxed">
              Está tardando más de lo normal. Revisá tu conexión y volvé a intentar.
            </p>
            <Button
              variant="accent"
              onClick={() => { setBootStalled(false); setSettingsRetryTick((t) => t + 1); }}
            >
              REINTENTAR
            </Button>
            <button
              onClick={() => window.location.reload()}
              className="text-xs font-bold text-white/60 underline underline-offset-2 hover:text-white/90"
            >
              Recargar la app
            </button>
          </div>
        )}
      </div>
    );
  }

  // /admin-predios o intento de login VENUE_ADMIN → pantalla informativa con
  // auto-redirect a predios.ajupam.ar/admin/login (5s).
  if (showVenueRedirect) {
    return <React.Suspense fallback={null}><VenueRedirectScreen /></React.Suspense>;
  }

  // --- PUBLIC ROUTES (Always accessible, ignores login) ---
  const currentPath = window.location.pathname;
  const wantsLogin = new URLSearchParams(window.location.search).get('login') === 'true';

  // --- PREVIEW ROUTES (v6.9.6) — demo sin API, solo acceso desktop ---
  if (currentPath.startsWith('/preview/opener')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewOpenerPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/recepcion')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewReceptionPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/entre-temporadas')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewBetweenSeasonsPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/placas')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewPlacasPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/alerta-presente')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewAlertaPresentePage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/mi-dia')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewMiDiaPage /></React.Suspense>;
  }

  if (currentPath.startsWith('/preview/credencial')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewCredencialPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/novedades')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewNovedadesPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/simulacro')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewSimulacroPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/propuesta-vencida')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewExpiredProposalPage /></React.Suspense>;
  }
  if (currentPath.startsWith('/preview/tarjeta-partido')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PreviewMatchCardPage /></React.Suspense>;
  }

  // --- SPONSOR ROUTES (v6.9.3) ---
  if (currentPath === '/sponsor/registrar') {
    return <React.Suspense fallback={ROUTE_FALLBACK}><SponsorRegistrationForm /></React.Suspense>;
  }
  if (currentPath === '/sponsor/portal') {
    return <React.Suspense fallback={ROUTE_FALLBACK}><SponsorPortal /></React.Suspense>;
  }

  // --- MATCH OPENER ROUTES (v6.8.0) ---
  // Login del juez de cancha + centro de control. Auth independiente del jugador y
  // del admin (token guardado en localStorage 'opener_session_token').
  // v6.11.208 — el rol se llama "juez de cancha": la ruta es `/juez`, y
  // `/abridor` sigue andando igual (links, QRs y pushes viejos).
  if (currentPath.startsWith('/juez') || currentPath.startsWith('/abridor')) {
    const openerLeagueSlug = new URLSearchParams(window.location.search).get('league');
    // v6.11.208 — /juez/login y /juez/control (y el alias /abridor/*) comparten el MISMO proveedor
    // y el mismo componente: pasar del login al centro de control ya no recarga
    // la página (antes: validar → reload → validar otra vez, y con el link
    // `/abridor?league=` y sesión viva, el doble arranque en cada apertura).
    return (
      <OpenerAuthProvider>
        <React.Suspense fallback={ROUTE_FALLBACK}>
          <OpenerControlOrLogin leagueSlug={openerLeagueSlug} />
        </React.Suspense>
      </OpenerAuthProvider>
    );
  }

  // --- RECEPTIONIST ROUTES (v6.9.4) ---
  // Panel de recepcionistas de mesa de control. Auth independiente (token en
  // localStorage 'reception_session_token').
  // Sitio de verificación de credenciales (control de acceso). Auth propia
  // (user_type='SECURITY'), token en localStorage, sin relación con la PWA.
  if (currentPath.startsWith('/verificacion')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><VerificationPage /></React.Suspense>;
  }

  // v6.11.344 — Encargado de sponsors. Auth propia (user_type='SPONSOR_MANAGER'),
  // token en localStorage. `patrocinadores` ya está en RESERVED_SLUGS.
  if (currentPath.startsWith('/patrocinadores')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><SponsorManagerPage /></React.Suspense>;
  }

  // QR de la credencial escaneado con la cámara nativa: redirige a la ficha
  // pública de la pareja. No verifica: eso vive en /verificacion.
  if (currentPath.startsWith('/v/')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><CredentialQrRedirect /></React.Suspense>;
  }

  // v6.11.372 — Sorteo en sede. `/sorteo/<código>/qr` es la pantalla del PERSONAL (QR grande para mostrar en el
  // celular); `/sorteo/<código>` es lo que lleva el QR del cartel: anota al jugador y le muestra «¡Te registraste!».
  if (currentPath.startsWith('/sorteo/')) {
    return (
      <React.Suspense fallback={ROUTE_FALLBACK}>
        {parseRaffleQrPath(currentPath) ? <RaffleQrScreenPage /> : <RaffleJoinPage />}
      </React.Suspense>
    );
  }

  if (currentPath.startsWith('/recepcion')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><ReceptionistPage /></React.Suspense>;
  }

  // /liga/* SIEMPRE muestra vista pública (incluso para participantes logueados)
  if (currentPath.startsWith('/liga')) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PublicRouter /></React.Suspense>;
  }

  // v6.11.15.00 — Root-level slug: `/<slug>` es equivalente a `/liga/<slug>`.
  // Un solo segmento kebab-case no reservado → landing por-edición. PublicRouter
  // reconoce el mismo patrón en parsePublicPath (fallback si el slug existe).
  // No aplica en PWA instalada — allí `/` va al dashboard del participante.
  if (isRootLevelLeagueSlug(currentPath) && !isInstalledPWA()) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PublicRouter /></React.Suspense>;
  }

  // "/" en browser (no PWA): siempre mostrar landing, con o sin sesión.
  // El browser y PWA pueden compartir localStorage (Android), así que no se puede
  // depender de !user para detectar si el contexto es browser o PWA.
  // En PWA (standalone): saltar landing y mostrar login/dashboard del participante.
  if (currentPath === '/' && !wantsLogin && !isInstalledPWA()) {
    return <React.Suspense fallback={ROUTE_FALLBACK}><PublicRouter /></React.Suspense>;
  }

  if (currentPath.startsWith('/partidos-liga')) {
    window.location.replace('/liga');
    return null;
  }

  // --- ADMIN DASHBOARD ---
  // IMPORTANTE: Admin sessions son independientes de user sessions.
  // VENUE_ADMIN ya no opera en Liga — esa rama se sacó en post-v6.9.0;
  // los venue admins ahora viven en predios.ajupam.ar/admin (ver
  // platform-ajupam/docs/liga-redirect-venue-admins.md).
  if (adminSessionToken && adminRole && (adminRole === 'ADMIN' || adminRole === 'SUPER_ADMIN')) {
    // AdminDashboard NO requiere user de participante, solo adminSessionToken.
    // LeagueProvider expone la liga activa + selector multi-liga (v6.5.0 Fase 3)
    return (
      <React.Suspense fallback={null}>
        <LeagueProvider adminSessionToken={adminSessionToken}>
          <AdminDashboard
            adminSessionToken={adminSessionToken}
            onLogout={handleLogout}
            initialAdminUserId={adminUserId}
            initialAdminRole={adminRole}
          />
        </LeagueProvider>
      </React.Suspense>
    );
  }

  // --- ADMIN LOGIN ---
  if (showAdminLogin) {
    return <React.Suspense fallback={null}><AdminLogin onSuccess={handleAdminLoginSuccess} onCancel={handleLogout} /></React.Suspense>;
  }

  // --- DESKTOP BLOCKING FOR PARTICIPANTS (v6.3.6 / v6.3.10) ---
  // Block desktop access to participant area, only allow mobile
  // Admin/venue routes are excluded from this check
  const path = window.location.pathname;
  const isAdminRoute = path.startsWith('/admin-liga') || path.startsWith('/admin-predios');
  // "Ruta pública" (accesible desde desktop) = landing/marketing: la home `/`, las
  // vistas `/liga/*` y las landings por-liga root-level (`/sur`). El ingreso de
  // participante ocurre en `/?login=true` (mismo path `/`) → NO es ruta pública:
  // el gate debe forzar la PWA en desktop igual que en cualquier vista de
  // participante. Sin el `!wantsLogin`, un participante podía loguearse con DNI y
  // entrar al área de jugador desde desktop (solo /admin-liga debe poder). La
  // landing pública en `/` (sin ?login) sigue accesible en desktop.
  const isPublicRoute = (path === '/' && !wantsLogin) || path.startsWith('/liga') || isRootLevelLeagueSlug(path);
  const isDesktop = !isMobileDevice();

  // v6.11.24 — El área de participante (dashboard en adelante) es exclusiva de la
  // app en el celular. El override manual `force-mobile-access` (v6.3.10) se
  // eliminó: era un escape que dejaba entrar desde una computadora o desde "vista
  // escritorio" del navegador, justo lo que hay que impedir. La detección
  // multi-capa de isMobileDevice() (UA + touch + viewport) ya cubre los celulares
  // reales en modo normal, así que no se necesita escape manual.
  const shouldBlockDesktop = isDesktop && !isAdminRoute && !isPublicRoute && !adminSessionToken && !isLocalhost;

  // (Se eliminó el console.log "Device Detection Debug": disparaba en cada
  // render e inundaba la consola en dev. La detección de dispositivo sigue igual.)

  if (shouldBlockDesktop) {
    return <MobileOnlyScreen />;
  }

  // --- IN-APP BROWSER ESCAPE (v6.3.0) ---
  // Detecta WebViews de apps sociales donde la instalación PWA es imposible.
  // Se muestra a todos los usuarios mobile (logueados o no) para que puedan
  // escapar al navegador real antes de cualquier flujo de auth.
  const isPWA = isInstalledPWA();
  const isInApp = isInAppBrowser();
  const shouldShowInAppWarning =
    !isDesktop && isInApp && !isPWA && !isAdminRoute && !isLocalhost && !dismissedInAppWarning;

  if (shouldShowInAppWarning) {
    return <InAppBrowserScreen onDismiss={() => setDismissedInAppWarning(true)} />;
  }

  // --- BLOQUEO: participante en liga activa desde navegador móvil → usar la app ---
  // Resuelto en el gate de DNI (handleLogin). Bloqueo duro, sin opción de seguir en
  // el navegador. Se ubica antes del hub/login/inscripción para cortar el flujo.
  if (mustUseApp) {
    return <BrowserBlockScreen reason="active_league" />;
  }

  // --- DEEP-LINK DE LIGA (usuario ya logueado) ---
  // Mientras el efecto de reconciliación resuelve a dónde mandar al usuario que
  // abrió el link de otra liga, mostramos un loader en vez del dashboard de su
  // edición vieja (evita el flash). `needsDeepLinkReconcile` se evalúa síncrono
  // para cubrir el primer render, antes de que el efecto marque el estado.
  const deepLinkLeagueParam = new URLSearchParams(window.location.search).get('league');
  const needsDeepLinkReconcile =
    !!user && !entryDni && !deepLinkReconciledRef.current && !!deepLinkLeagueParam &&
    (((team as any)?.league?.slug ?? null) !== deepLinkLeagueParam);

  if (user && (needsDeepLinkReconcile || reconcilingLeague)) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-2 border-primary/30 border-t-primary rounded-full" />
      </div>
    );
  }

  // Inscripción de la liga del link cerrada y el jugador no participa de ella:
  // aviso claro y puente a su edición actual (limpiar el aviso → cae al dashboard).
  if (user && leagueClosedNotice) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6 text-center">
        <div className="bg-surface p-8 rounded-2xl shadow-card border border-gray-100 max-w-sm w-full">
          <div className="w-14 h-14 rounded-2xl bg-warning-bg text-warning flex items-center justify-center mx-auto mb-5">
            <AlertTriangle size={28} />
          </div>
          <h2 className="text-xl font-display font-black text-text-main mb-2 uppercase tracking-tight">
            Inscripción cerrada
          </h2>
          <p className="text-sm text-text-muted mb-6 leading-relaxed">
            La inscripción de <span className="font-bold text-text-main">{leagueClosedNotice}</span> no está abierta
            en este momento, y todavía no formás parte de esa edición.
          </p>
          <Button fullWidth size="lg" onClick={() => setLeagueClosedNotice(null)}>
            Ir a mi edición actual
          </Button>
        </div>
      </div>
    );
  }

  // --- SUBSTITUTE REGISTRATION (Priority) ---
  if (view === 'substitute_registration' && substitutionToken) {
    return (
      <SubstituteRegistration 
        token={substitutionToken} 
        onComplete={() => {
          setSubstitutionToken(null);
          window.history.pushState({}, '', '/');
          setView('dashboard');
        }} 
      />
    );
  }

  // --- PUBLIC LANDING ---
  if (!user) {
    // v6.11.4 (Fase 9) — Hub multi-liga: el jugador con varias opciones elige a dónde entrar
    if (entryOptions) {
      return (
        <PlayerLeagueHub
          options={entryOptions}
          onEnterLeague={handleEnterLeague}
          onRegister={handleRegisterOpen}
          onLogout={() => { setEntryOptions(null); setDniInput(''); setEntryDni(null); }}
        />
      );
    }
    // v6.11.17.03 — `!enrollNewEdition` en el guard: si el jugador ya fue ruteado
    // al flujo de inscripción de una edición abierta (get_player_entry_options),
    // NO debemos volver a mostrar el formulario de DNI. En multi-liga `settings` es
    // null → `!settings?.is_registration_active` es true y, sin este guard, el
    // bloque rebotaba al login por DNI en loop (caía el flujo de enroll). Con el
    // guard, cae al render de Registration más abajo (shouldShowRegistration).
    if ((loginMode || !settings?.is_registration_active) && !registrationAllowedByJoin && !enrollNewEdition) {
      return (
        <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-6 text-center animate-fade-in">
          <div className="bg-surface p-8 rounded-2xl shadow-card border border-gray-100 max-w-sm w-full">
            <div className="mb-6">
              <img
                src="https://muincpkiwzhuhqfhgzfa.supabase.co/storage/v1/object/public/APP/IconApp/IconApp.svg"
                alt="Liga AJUPAM"
                className="h-32 w-auto object-contain mx-auto animate-fade-in-down"
              />
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                placeholder="DNI (8 dígitos)"
                value={dniInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Solo permitir números
                  if (/^\d*$/.test(value)) {
                    setDniInput(value);
                  }
                }}
                className="w-full border-2 border-gray-200 p-3 mb-4 rounded-xl outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
              {loginError && <p className="mb-4 text-red-600 text-sm font-medium">{loginError}</p>}
              <Button fullWidth type="submit" isLoading={isLoading}>CONTINUAR</Button>
            </form>
            {settings?.is_registration_active && (
              <Button variant="ghost" className="mt-4" onClick={() => setLoginMode(false)}>
                ← CANCELAR
              </Button>
            )}
          </div>
        </div>
      );
    }
    if (!dniInput) {
      return (
        <div className="min-h-screen bg-primary flex flex-col items-center justify-center p-6 text-white text-center relative overflow-hidden">
          {/* Fondo con patrón sutil */}
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/path/to/subtle-pattern.svg)', backgroundSize: 'cover' }}></div>

          <div className="relative z-10 flex flex-col items-center">
            <div className="mb-8">
              <img
                src={(settings as any)?.league_logo_url || DEFAULT_LIGA_LOGO}
                alt="Liga AJUPAM"
                className="h-40 w-auto object-contain mx-auto animate-fade-in-down"
                style={{ filter: getLogoFilter((settings as any)?.league_logo_treatment, 'dark') || undefined }}
              />
            </div>
            {settings?.is_registration_active && (
              <div className="mb-4 inline-flex items-center gap-2 bg-secondary text-primary font-black uppercase tracking-wider text-xs px-4 py-2 rounded-full shadow-lg animate-fade-in-down animation-delay-200">
                <Megaphone size={16} />
                Inscripción abierta
              </div>
            )}
            <p className="mb-4 font-bold tracking-widest uppercase text-lg animate-fade-in-down animation-delay-400 text-secondary">
              {settings?.season_name}
            </p>

           {/* Carousel de Anuncios Moderno (lazy: no bloquea el paint del login) */}
           {announcements.length > 0 && (
             <React.Suspense fallback={null}>
               <LoginAnnouncementsCarousel announcements={announcements} />
             </React.Suspense>
           )}

           <Button
             variant="accent"
             size="lg"
             className="mt-8 px-12 h-16 text-xl shadow-2xl animate-fade-in-up animation-delay-600"
             onClick={() => setLoginMode(true)}
           >
             INGRESAR <ArrowRight className="ml-2" />
           </Button>
           {settings?.is_registration_active && (
             <p className="mt-4 text-sm text-white/80 font-medium max-w-xs animate-fade-in-up animation-delay-600">
               Ingresá con tu DNI para inscribirte en la nueva edición o ver tu equipo.
             </p>
           )}
          </div>
        </div>
      );
    }
  }

  // --- REGISTRATION ---
  // Mostrar registro solo para:
  // 1. Usuarios nuevos: !user (DNI no existe en DB)
  // 2. Jugadores huérfanos con código de invitación: user && !team && joinCode
  // NO mostrar para jugadores huérfanos sin código → van al dashboard
  // EXCEPCIÓN: cuando las inscripciones están cerradas pero hay un joinCode válido,
  //   se permite completar la pareja de un equipo INCOMPLETO (registrationAllowedByJoin)
  //   o unirse si el usuario ya existe pero no tiene equipo (isOrphanPlayerWithInvite)
  const isNewUser = !user && dniInput;
  // Jugador logueado que abrió un link de invitación /join/<code>. Cubre dos casos:
  //   - huérfano (sin equipo) — comportamiento previo (isOrphanPlayerWithInvite).
  //   - YA con equipo en OTRA edición (típico: instaló la app en una edición pasada
  //     y el capitán le pasa el link de la nueva) → antes caía a su dashboard viejo
  //     ("no aparece nada"); ahora va al flujo de unirse al equipo de la nueva
  //     edición. El backend (user_join_team) mueve su current_team_id al nuevo
  //     equipo; joinCode se limpia al terminar (onTeamRegistered) para no reentrar.
  const isJoiningViaInvite = !!user && !!joinCode;
  // v6.11.12 — enrollNewEdition habilita el gate por sí solo: el jugador eligió
  // (o fue ruteado a) inscribirse en una liga con inscripción abierta, validada por
  // get_player_entry_options. No debe depender del `settings` de bootstrap, que es
  // null cuando hay ≥2 ligas enabled (multi-liga real).
  const shouldShowRegistration =
    (settings?.is_registration_active || registrationAllowedByJoin || isJoiningViaInvite || enrollNewEdition) &&
    (isNewUser || isJoiningViaInvite || enrollNewEdition) &&
    !showRegistrationSuccess;

  if (shouldShowRegistration) {
    return (
      <Layout
        currentUser={user ?? undefined}
        currentView="registration"
        isSeasonActive={false}
        whatsappSupport={settings?.whatsapp_support}
        regulationsUrl={settings?.regulations_url}
        leagueSlug={leagueSlug}
        hideBottomNav={true}
        aiAssistantEnabled={settings?.ai_assistant_enabled}
        aiAssistantOpenaiId={settings?.ai_assistant_openai_id}
        onNavigate={() => {}}
        onLogout={handleLogout}
      >
        <Registration user={user ? undefined : undefined} dni={user?.dni || entryDni || parseInt(dniInput)} onTeamRegistered={async (isCreation) => { await updateTeam(); setJoinCode(null); if (isCreation) setShowRegistrationSuccess(true); else setView("dashboard"); }} prefilledInviteCode={joinCode} leagueSlug={enrollLeagueSlug || leagueSlug} />
      </Layout>
    );
  }

  if (showRegistrationSuccess && team && user) {
    return <RegistrationSuccess team={team} settings={settings} user={user} onContinue={() => { setShowRegistrationSuccess(false); setView('dashboard'); }} />;
  }

  // --- LIGA NO PUBLICADA (v6.11.304) ---
  // El único equipo del jugador es de una liga en borrador: no se muestra hasta
  // que la liga se publique (loginWithDNI lo saltea y deja el nombre en la sesión).
  const unpublishedLeague = user && !team ? getCurrentSession()?.unpublishedLeague : undefined;
  if (unpublishedLeague) {
    return (
      <UnpublishedLeagueScreen
        playerName={user!.full_name}
        leagueName={unpublishedLeague}
        whatsappSupport={settings?.whatsapp_support}
        onLogout={handleLogout}
      />
    );
  }

  // --- REPLACED PLAYER SCREEN ---
  // v6.11.318 — por edición: sólo si no tiene equipo en ninguna (ver replacedInfo).
  if (user && !team && replacedInfo) {
    return (
      <ReplacedPlayerScreen
        playerName={user.full_name}
        teamName={replacedInfo.team_name}
        leagueName={replacedInfo.name}
        leagueSlug={replacedInfo.slug}
        whatsappSupport={settings?.whatsapp_support}
        onLogout={() => { setReplacedInfo(null); handleLogout(); }}
      />
    );
  }

  // --- DASHBOARD / SEASON ---
  if (team && team.status === 'ELIMINADO') {
    return <RecoveryScreen team={team} onReactivated={updateTeam} />;
  }

  // --- EDICIÓN FINALIZADA (v6.10.0.29) ---
  // El participante cuya edición terminó ve una pantalla "tu edición finalizó"
  // con su historial read-only + puente a la nueva edición (si hay inscripción
  // abierta). Si elige inscribirse, enrollNewEdition activa el flujo de Registration.
  // v6.11.4: en modo solo-lectura (Fase 9) mostramos el dashboard de la edición
  // finalizada en lugar de esta pantalla. La SeasonEndedScreen queda como fallback
  // para sesiones viejas (sin readOnly) hasta que el jugador vuelva a ingresar.
  if (user && team && (team as any).league?.editionFinished && !enrollNewEdition && !readOnly) {
    return (
      <SeasonEndedScreen
        userName={user.full_name}
        team={team}
        // v6.11.28.02 — De la liga ACTIVA de su familia, no de la edición
        // archivada que está viendo (ahí `is_registration_active` es siempre
        // false → el puente a la edición nueva nunca aparecía). Fallback al
        // comportamiento previo si la familia no resolvió.
        newEditionOpen={nextEdition?.registrationOpen ?? !!settings?.is_registration_active}
        newEditionName={nextEdition?.name ?? settings?.season_name}
        // v6.11.62 — Con el slug de la edición nueva: Registration lo necesita
        // para saber a qué liga se está inscribiendo (categorías, settings y
        // reglamento). Sin él el formulario quedaba sin Rama ni Categoría.
        onEnroll={() => { if (nextEdition?.slug) setEnrollLeagueSlug(nextEdition.slug); setEnrollNewEdition(true); }}
        onLogout={handleLogout}
      />
    );
  }

  // --- BROWSER BLOCKING FOR MOBILE (v4.10.1) ---
  // Block mobile browser access to dashboard, only allow PWA
  // Registration flow is allowed from browser
  const isMobile = isMobileDevice();
  const shouldBlockBrowser = isMobile && !isPWA && user && user.current_team_id && !isLocalhost;

  if (shouldBlockBrowser) {
    // v6.11.41.00 — `?from=push` lo agrega el Service Worker al deep-link de la
    // notificación. Si llegamos acá con esa marca, el usuario tiene la app sí o
    // sí (las push solo se suscriben desde la PWA): lo que falló es que el
    // navegador se quedó con el link. Ofrecerle "instalá la app" desorienta.
    const cameFromPush = new URLSearchParams(window.location.search).get('from') === 'push';
    return <BrowserBlockScreen userName={user.full_name} reason={cameFromPush ? 'push' : 'default'} />;
  }

  if (matchIdToEdit && team) {
    return <MatchCenter matchId={matchIdToEdit} teamId={team.id} onClose={() => setMatchIdToEdit(null)} />;
  }

  // v6.11.46 — el bloqueo ya no es sólo el flag manual del admin: con la
  // temporada en curso también bloquean el equipo sin compañero y la deuda de
  // inscripción. La regla (y su prioridad) vive en utils/blockState.
  const blockState = resolveBlockState({
    userIsBlocked: user?.is_blocked,
    teamIsBlocked: team?.is_blocked,
    teamStatus: team?.status,
    teamPartnerId: team?.partner_id,
    teamAmountPaid: team?.amount_paid,
    teamDiscountAmount: team?.discount_amount,
    registrationFee: settings?.registration_fee,
    isSeasonActive: settings?.is_season_active,
    isBracketPublished,
    autoBlockExempt: team?.auto_block_exempt,
  });

  // v6.11.48 — una sola decisión para los tres casos en los que el jugador
  // todavía no está compitiendo (pre-temporada, lista de espera, bloqueo):
  // el Inicio pasa a la vista de inscripción —donde se regulariza— y lo que
  // se cierra son Tabla y Partidos. Ver utils/sectionAccess. Vive fuera de
  // renderView porque el nav inferior candadea las mismas secciones.
  const access = resolveSectionAccess({
    isSeasonActive: settings?.is_season_active,
    isBracketPublished,
    readOnly,
    teamStatus: team?.status,
    block: blockState,
  });

  const viewerLeagueId: string | null = (team as any)?.league_id ?? null;

  const renderView = () => {
    if (!user) return null;
    const isBlocked = blockState.blocked;

    /** Envuelve una sección de competencia en el velo cuando corresponde. */
    const gated = (node: React.ReactNode) =>
      access.seasonSectionsLocked ? (
        <LockedSectionOverlay
          reason={access.reason}
          regularPhaseStartDate={settings?.regular_phase_start_date}
          onGoHome={() => setView('dashboard')}
        >
          {node}
        </LockedSectionOverlay>
      ) : node;

    switch (view) {
      case 'dashboard':
        // CRITICAL FIX: Pass team without non-null assertion (PreSeasonDashboard now handles null)
        // v6.11.4: en modo solo-lectura (edición finalizada) mostramos el Dashboard completo
        // (datos + modales) en lugar del PreSeasonDashboard.
        if (access.inscriptionHome) {
          return <PreSeasonDashboard user={user} team={team} onTeamUpdate={updateTeam} blockState={blockState} onNavigate={navigate} />;
        }
        return <Dashboard user={user} onGoToResults={(id) => setMatchIdToEdit(id)} onNavigate={navigate} isBlocked={isBlocked} />;
      case 'standings':
        return gated(<Standings onNavigate={navigate} />);
      case 'schedule': {
        if (isBracketPublished && team) {
          const sess = getCurrentSession();
          // Slot "Partidos": sub-pestañas Playoffs | Fase Regular (la fase regular sigue
          // accesible aunque el bracket esté publicado / la liga finalizada).
          return sess?.token ? gated(
            <PartidosTabs
              categoryId={team.category_id}
              sessionToken={sess.token}
              onOpenMatchCenter={(id) => setMatchIdToEdit(id)}
              onNavigate={navigate}
            />
          ) : null;
        }
        return gated(
          <Fixture
            onOpenMatchCenter={(id) => setMatchIdToEdit(id)}
            onNavigate={navigate}
            preselectOpponentId={preselectOpponentId}
            onPreselectOpponentConsumed={() => setPreselectOpponentId(null)}
          />
        );
      }
      case 'schedule-agenda':
        return gated(
          <Fixture
            initialTab="scheduling"
            initialScheduleTab="agenda"
            onOpenMatchCenter={(id) => setMatchIdToEdit(id)}
            onNavigate={navigate}
            highlightMatchId={matchIdToHighlight}
            onHighlightConsumed={() => setMatchIdToHighlight(null)}
          />
        );
      case 'scheduling':
        return team ? gated(<ScheduleMatch
          teamId={team.id}
          teamName={team.name}
          isBlocked={isBlocked}
          onNavigateToResults={(id) => setMatchIdToEdit(id)}
          regularPhaseStartDate={settings?.regular_phase_start_date}
          regularPhaseEndDate={settings?.regular_phase_end_date}
        />) : null;
      case 'team':
        return <TeamView onGoToResults={(id) => setMatchIdToEdit(id)} onNavigate={navigate} />;
      case 'profile':
        return <ProfileView onLogout={handleLogout} onSwitchEdition={handleSwitchEdition} />;
      case 'convenios':
        return <ConveniosInfo onBack={() => setView('dashboard')} />;
      case 'simulacro':
        // Autocontenido: no toca datos reales, así que no va envuelto en `gated`
        // ni depende de que la competencia esté abierta. Quien llega hasta acá
        // es porque tocó la carta del Inicio, y practicar nunca puede estar
        // candadeado.
        return (
          <Simulacro
            leagueSlug={(team as any)?.league?.slug ?? leagueSlug}
            onFinish={() => {
              const token = getCurrentSession()?.token;
              if (token) markSimulacroCompleted(token, SIMULACRO_VERSION);
              simulacroGate.markDone();
              setView('dashboard');
            }}
            onExit={() => setView('dashboard')}
          />
        );
      case 'league-event':
        // v6.11.225 — Evento intermedio. Sin `gated`: la tarjeta aparece también
        // en pre-temporada, y el backend decide quién puede inscribirse.
        return eventIdToOpen ? (
          <LeagueEventScreen
            key={eventIdToOpen}
            eventId={eventIdToOpen}
            // La URL `/evento/<id>` y el id los limpia el efecto de arriba al salir.
            onBack={() => setView('dashboard')}
          />
        ) : (
          <Dashboard user={user} onGoToResults={(id) => setMatchIdToEdit(id)} isBlocked={isBlocked} onNavigate={navigate} />
        );
      case 'playoffs-bracket':
        const currentSession = getCurrentSession();
        return team && currentSession?.token ? gated(
          <PlayoffBracketView
            categoryId={team.category_id}
            sessionToken={currentSession.token}
          />
        ) : null;
      default:
        return <Dashboard user={user} onGoToResults={(id) => setMatchIdToEdit(id)} isBlocked={isBlocked} onNavigate={navigate} />;
    }
  };

  return (
    // v6.11.55 — La liga del jugador viaja por contexto. Los hooks compartidos
    // (`useShareBackgrounds`, y cualquiera que venga después) la resuelven desde
    // acá en vez de asumir que "sin liga la RPC resuelve sola": con más de una
    // liga `enabled` no resuelve, lanza.
    <ViewerLeagueProvider leagueId={viewerLeagueId}>
      {/* v6.11.329 — Encuestas del jugador, una sola vez para toda la app. La
          `key` vuelve a pedirlas al cambiar de edición. */}
      <SurveyStatusProvider
        key={viewerLeagueId ?? 'sin-edicion'}
        enabled={!!user && !!team && !readOnly}
      >
      <Layout
        currentUser={user ?? undefined}
        currentView={view}
        isSeasonActive={settings?.is_season_active ?? false}
        isBracketPublished={isBracketPublished}
        regularPhaseStartDate={settings?.regular_phase_start_date}
        readOnly={readOnly}
        seasonSectionsLocked={access.seasonSectionsLocked}
        lockReason={access.reason}
        whatsappSupport={settings?.whatsapp_support}
        regulationsUrl={settings?.regulations_url}
        leagueSlug={leagueSlug}
        termsPrivacyUrl={settings?.terms_privacy_url}
        aiAssistantEnabled={settings?.ai_assistant_enabled}
        aiAssistantOpenaiId={settings?.ai_assistant_openai_id}
        sessionToken={team?.status === 'ACTIVO' ? getCurrentSession()?.token : undefined}
        onNavigate={navigate}
        onOpenMatch={(id) => setMatchIdToEdit(id)}
        onHighlightMatch={(id) => setMatchIdToHighlight(id)}
        onLogout={handleLogout}
      >
        {/* CTA llamativo (colores institucionales) en el INICIO de una edición
            archivada: si hay una edición posterior de la misma familia con
            inscripción abierta, ofrece anotarse directo. Le da un camino claro a
            quien entra a su edición vieja (incl. el que llega por un link) y no
            sabía qué hacer. */}
        {readOnly && view === 'dashboard' && user?.dni && (
          <NewEditionCta
            dni={user.dni}
            currentLeagueId={(team as any)?.league_id}
            onEnroll={(open) => { setEnrollLeagueSlug(open.slug); setEnrollNewEdition(true); }}
          />
        )}
        {readOnly && (
          <div className="mx-4 my-3 flex items-center justify-between gap-3 bg-warning-bg border border-warning/20 rounded-xl px-3.5 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <Eye size={13} className="text-warning shrink-0" />
              <p className="text-xs font-semibold text-warning truncate">
                Estás viendo <span className="font-bold">{(team as any)?.league?.name ?? 'una edición finalizada'}</span> — solo lectura
              </p>
            </div>
            <button
              onClick={() => { setEntryOptions(null); logout(); }}
              className="text-xs font-bold text-warning underline underline-offset-2 shrink-0 whitespace-nowrap hover:opacity-80"
            >
              Cambiar de liga
            </button>
          </div>
        )}
        {/* v6.11.68 — Pedido de leer el reglamento durante los primeros 5 días
            con la app entera habilitada. Sólo en el Inicio: es un recordatorio,
            no un cartel para arrastrar por todas las pantallas. La ventana la
            decide utils/rulebookCtaWindow (npm run test:rulebook-cta).
            v6.11.74 — La misma tarjeta ofrece el Simulacro, con su propia
            vigencia: el pedido de leer dura 5 días, el simulacro dura hasta que
            esté hecho. Ver el comentario de RulebookCta.
            v6.11.94 — Y con su propio interruptor del admin. */}
        {view === 'dashboard' && team && (
          <RulebookCta
            regularPhaseStartDate={settings?.regular_phase_start_date}
            teamCreatedAt={(team as any)?.created_at}
            seasonSectionsLocked={access.seasonSectionsLocked}
            dataLoaded={!!settings && !!team}
            leagueSlug={(team as any)?.league?.slug ?? leagueSlug}
            regulationsUrl={settings?.regulations_url}
            ctaEnabled={settings?.rulebook_cta_enabled}
            ctaStartDate={settings?.rulebook_cta_start_date}
            ctaEndDate={settings?.rulebook_cta_end_date}
            simulacroCtaEnabled={settings?.simulacro_cta_enabled}
            simulacroPending={
              simulacroGate.pending && access.seasonSectionsLocked === false && !!settings
            }
            onOpenSimulacro={() => {
              trackAppEvent('simulacro_start');
              setView('simulacro');
            }}
          />
        )}
        {/* v6.11.329 — Encuesta obligatoria: tapa todas las secciones menos
            Perfil hasta responderla (ver MandatorySurveyGate). */}
        <MandatorySurveyGate active={view !== 'profile'} bracketPublished={isBracketPublished}>
          <React.Suspense fallback={ROUTE_FALLBACK}>
            {renderView()}
          </React.Suspense>
        </MandatorySurveyGate>
      </Layout>
      </SurveyStatusProvider>
      {/* Gate de re-aceptación del reglamento: modal bloqueante SOLO si el usuario
         tiene needs_reaccept (el admin lo forzó o cambió la versión publicada).
         Grandfathered y errores de red → NO bloquea. Dormido hasta que se fuerce o
         se bumpee la versión del reglamento. */}
      {user && !user.is_admin && (
        <RulebookReacceptanceGate
          sessionToken={getCurrentSession()?.token}
          leagueSlug={leagueSlug}
          leagueId={(team as any)?.league_id}
        />
      )}
      {/* Push Notification Reminder - Shows on every PWA open if not enabled (v4.10.1) */}
      {isPWA && <PushNotificationReminder />}
      <Analytics />
    </ViewerLeagueProvider>
  );
}

// =============================================================================
// OpenerControlOrLogin (v6.8.0)
// =============================================================================
// Helper para /juez/control: si hay sesión → MatchOpenerControl; si no →
// redirigir a MatchOpenerLogin. Vive dentro de <OpenerAuthProvider>.
//
// v6.11.208 — `isLoading` es SÓLO el arranque: volver a primer plano ya no
// muestra "Validando sesión…" ni desmonta el centro de control. El login deja
// la URL en /juez/control sin recargar.
const OpenerControlOrLogin: React.FC<{ leagueSlug: string | null }> = ({ leagueSlug }) => {
  const { opener, isLoading, connectionError, retry } = useOpenerAuth();

  useEffect(() => {
    if (opener && !window.location.pathname.startsWith('/juez/control')) {
      window.history.replaceState({}, '', `/juez/control${window.location.search}`);
    }
  }, [opener]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-sm">Validando sesión...</div>
      </div>
    );
  }
  if (!opener && connectionError) {
    return <StaffConnectionError onRetry={retry} />;
  }
  if (!opener) {
    // Al loguear, el proveedor ya tiene la sesión y este componente pasa solo
    // al centro de control: no hace falta hacer nada más.
    return <MatchOpenerLogin leagueSlug={leagueSlug} onSuccess={() => { /* re-render por contexto */ }} />;
  }
  return <MatchOpenerControl />;
};

// v6.11.235 — /render/story/:id la abre el worker de historias (Chromium sin
// cabeza) para fotografiar una placa. Se resuelve ANTES que el resto de la app,
// como /preview/placas pero sin pasar por LeagueApp: sin auth, sin settings de
// la liga, sin anuncios ni diccionarios, sin loader de arranque. La placa trae
// su liga en el payload (o la del fixture), y en modo `?fixture=` la única
// consulta a la base es la de los sponsors del pie.
const IS_RENDER_STORY_ROUTE = window.location.pathname.startsWith('/render/story/');

function App() {
  if (IS_RENDER_STORY_ROUTE) {
    // Sin fallback visible: el worker espera __STORY_READY__, no un spinner.
    return <React.Suspense fallback={null}><RenderStoryPage /></React.Suspense>;
  }
  return <LeagueApp />;
}

export default App;
